import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Descriptions,
  Input,
  Select,
  DatePicker,
  message,
  Spin,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  EyeOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  TransactionOutlined,
  RollbackOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { Form, Input as AntInput } from 'antd';
import axios from 'axios';
import moment from 'moment';

const { RangePicker } = DatePicker;

// Create axios instance with JWT authentication (for admin)
const adminApi = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// Request interceptor to add JWT token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);
const { Search } = Input;
const { Option } = Select;

const Transactions = () => {
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: null,
    dateRange: null,
    paymentMethod: null,
    search: '',
    merchantId: null
  });
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [stats, setStats] = useState({ total: 0, success: 0, pending: 0, failed: 0 });
  const [reversalModalVisible, setReversalModalVisible] = useState(false);
  const [reversalTarget, setReversalTarget] = useState(null);
  const [reversalLoading, setReversalLoading] = useState(false);
  const [reversalForm] = Form.useForm();


  useEffect(() => {
    fetchTransactions();
    fetchPaymentMethods();
    fetchStats();
    fetchMerchants();
  }, [pagination.current, pagination.pageSize, filters]);

  const fetchTransactions = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching transactions with filters:', filters);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize
      };

      if (filters.status) params.status = filters.status;
      if (filters.dateRange) {
        params.date_from = filters.dateRange[0].format('YYYY-MM-DD');
        params.date_to = filters.dateRange[1].format('YYYY-MM-DD');
      }
      if (filters.paymentMethod) params.payment_method = filters.paymentMethod;
      if (filters.search) params.search = filters.search;
      if (filters.merchantId) params.merchant_id = filters.merchantId;

      console.log('Request params:', params);
      const response = await adminApi.get('/transactions', { params });
      console.log('Response:', response.data);
      setTransactions(response.data.transactions);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total
      }));
    } catch (error) {
      setError(error.message || 'Gagal memuat data transaksi');
      message.error('Gagal memuat data transaksi');
      console.error('Fetch transactions error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await adminApi.get('/payment-methods');
      setPaymentMethods(response.data);
    } catch (error) {
      console.error('Fetch payment methods error:', error);
    }
  };

  const handleExportCSV = () => {
    const params = new URLSearchParams();
    if (filters.search)       params.set('search', filters.search);
    if (filters.status)       params.set('status', filters.status);
    if (filters.paymentMethod) params.set('payment_method', filters.paymentMethod);
    if (filters.merchantId)   params.set('merchant_id', filters.merchantId);
    if (filters.dateRange?.[0]) params.set('date_from', filters.dateRange[0].format('YYYY-MM-DD'));
    if (filters.dateRange?.[1]) params.set('date_to', filters.dateRange[1].format('YYYY-MM-DD'));
    const token = localStorage.getItem('access_token');
    const url = `/api/reports/export/csv?${params.toString()}`;
    // Gunakan fetch agar bisa kirim Authorization header
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `transaksi_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
      })
      .catch(() => message.error('Gagal export CSV'));
  };

  const fetchMerchants = async () => {
    try {
      const response = await adminApi.get('/master-products/approved-merchants');
      setMerchants(response.data.data || []);
    } catch (error) {
      console.error('Fetch merchants error:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminApi.get('/transactions/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Fetch stats error:', error);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleReversal = (record) => {
    setReversalTarget(record);
    reversalForm.resetFields();
    setReversalModalVisible(true);
  };

  const submitReversal = async (values) => {
    setReversalLoading(true);
    try {
      const res = await adminApi.post(`/transactions/${reversalTarget.transaction_id}/reversal`, {
        reason: values.reason
      });
      message.success(`Reversal berhasil — sumber: ${res.data.source === 'pending_balance' ? 'Pending Balance' : 'Available Balance'}`);
      setReversalModalVisible(false);
      fetchTransactions();
      fetchStats();
    } catch (error) {
      message.error(error.response?.data?.error || 'Gagal memproses reversal');
    } finally {
      setReversalLoading(false);
    }
  };

  const showTransactionDetail = async (transactionId) => {
    try {
      const response = await adminApi.get(`/transactions/${transactionId}`);
      setSelectedTransaction(response.data);
      setDetailModalVisible(true);
    } catch (error) {
      message.error('Gagal memuat detail transaksi');
      console.error('Fetch transaction detail error:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'green';
      case 'pending':
        return 'orange';
      case 'failed':
        return 'red';
      case 'cancelled':
        return 'default';
      case 'expired':
        return 'volcano';
      case 'refunded':
        return 'purple';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'success':
        return 'Berhasil';
      case 'pending':
        return 'Menunggu';
      case 'failed':
        return 'Gagal';
      case 'cancelled':
        return 'Dibatalkan';
      case 'expired':
        return 'Kadaluarsa';
      case 'refunded':
        return 'Dikembalikan';
      default:
        return status;
    }
  };

  const columns = [
    {
      title: 'ID Transaksi',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
      width: 150,
    },
    {
      title: 'Order ID Mitra',
      dataIndex: 'external_id',
      key: 'external_id',
      width: 180,
      render: (val) => val || '-',
    },
    {
      title: 'Mitra',
      dataIndex: 'merchant_name',
      key: 'merchant_name',
      width: 150,
      render: (name) => name || '-',
    },
    {
      title: 'ID Pelanggan',
      dataIndex: 'customer_no',
      key: 'customer_no',
      width: 150,
      render: (no) => no || '-',
    },
    {
      title: 'Nama Pelanggan',
      dataIndex: 'destination_name',
      key: 'destination_name',
      width: 200,
      render: (name) => name || '-',
    },
    {
      title: 'Jumlah',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      render: (amount, record) => (
        <div>
          <div>{formatCurrency(amount)}</div>
          {record.fee > 0 && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              Fee: {formatCurrency(record.fee)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 150,
      render: (amount) => (
        <strong>{formatCurrency(amount)}</strong>
      ),
    },
    {
      title: 'Net Merchant',
      key: 'net_merchant',
      width: 150,
      render: (_, record) => {
        const net = Number(record.total_amount || 0) - Number(record.fee || 0) - Number(record.fee_system || 0);
        return <strong style={{ color: '#52c41a' }}>{formatCurrency(net)}</strong>;
      },
    },
    {
      title: 'Metode',
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 150,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Tanggal',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date) => moment(date).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      title: 'Aksi',
      key: 'action',
      fixed: 'right',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => showTransactionDetail(record.transaction_id)}
          >
            Detail
          </Button>
          {(record.status === 'success' || record.status === 'paid') && record.transaction_type === 'cash_in' && (
            <Button
              type="link"
              danger
              icon={<RollbackOutlined />}
              onClick={() => handleReversal(record)}
            >
              Reversal
            </Button>
          )}
        </Space>
      ),
    },
  ];

  console.log('Transactions render: loading =', loading, 'transactions.length =', transactions.length, 'error =', error);

  if (error) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <h2>Error</h2>
        <p>{error}</p>
        <Button onClick={fetchTransactions}>Coba Lagi</Button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1>Manajemen Transaksi</h1>
        <p style={{ color: '#666' }}>Kelola semua transaksi Arendo</p>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8} md={6}>
          <Card>
            <Statistic
              title="Total Transaksi"
              value={stats.total}
              prefix={<TransactionOutlined />}
            />
            <div style={{ marginTop: 4, fontSize: 12, color: '#94a3b8' }}>Bulan ini</div>
          </Card>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Card>
            <Statistic
              title="Berhasil"
              value={stats.success}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
            <div style={{ marginTop: 4, fontSize: 12, color: '#94a3b8' }}>Bulan ini</div>
          </Card>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Card>
            <Statistic
              title="Menunggu"
              value={stats.pending}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
            <div style={{ marginTop: 4, fontSize: 12, color: '#94a3b8' }}>Bulan ini</div>
          </Card>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Card>
            <Statistic
              title="Gagal"
              value={stats.failed}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
            <div style={{ marginTop: 4, fontSize: 12, color: '#94a3b8' }}>Bulan ini</div>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="Cari transaksi..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter Status"
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="success">Berhasil</Option>
              <Option value="pending">Menunggu</Option>
              <Option value="processing">Diproses</Option>
              <Option value="failed">Gagal</Option>
              <Option value="cancelled">Dibatalkan</Option>
              <Option value="expired">Kadaluarsa</Option>
              <Option value="refunded">Dikembalikan</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter Metode"
              value={filters.paymentMethod}
              onChange={(value) => setFilters(prev => ({ ...prev, paymentMethod: value }))}
              style={{ width: '100%' }}
              allowClear
            >
              {paymentMethods.map(method => (
                <Option key={method.id} value={method.name}>
                  {method.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              placeholder={['Dari tanggal', 'Sampai tanggal']}
              value={filters.dateRange}
              onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              showSearch
              placeholder="Filter Mitra"
              value={filters.merchantId}
              onChange={(value) => setFilters(prev => ({ ...prev, merchantId: value }))}
              style={{ width: '100%' }}
              allowClear
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {merchants.map(m => (
                <Option key={m.id} value={m.id}>
                  {m.full_name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Transactions Table */}
      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Daftar Transaksi</h3>
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportCSV}
            >
              Export CSV
            </Button>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={fetchTransactions}
            >
              Refresh
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="transaction_id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} transaksi`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Reversal Modal */}
      <Modal
        title={<span style={{ color: '#cf1322' }}><RollbackOutlined /> Reversal Transaksi</span>}
        open={reversalModalVisible}
        onCancel={() => setReversalModalVisible(false)}
        footer={null}
        width={500}
      >
        {reversalTarget && (
          <>
            <div style={{ background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 6, padding: 12, marginBottom: 16 }}>
              <div><strong>Transaction ID:</strong> {reversalTarget.transaction_id}</div>
              <div><strong>Jumlah:</strong> {formatCurrency(reversalTarget.total_amount || reversalTarget.amount)}</div>
              <div style={{ marginTop: 8, color: '#cf1322', fontSize: 13 }}>
                Dana akan dipotong dari saldo merchant. Jika sudah release ke available balance, saldo bisa menjadi minus.
              </div>
            </div>
            <Form form={reversalForm} layout="vertical" onFinish={submitReversal}>
              <Form.Item
                label="Alasan Reversal"
                name="reason"
                rules={[{ required: true, message: 'Alasan wajib diisi' }]}
              >
                <Input.TextArea rows={3} placeholder="Contoh: Supplier konfirmasi transaksi gagal pada 14 Mei 2026" />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => setReversalModalVisible(false)}>Batal</Button>
                  <Button type="primary" danger htmlType="submit" loading={reversalLoading} icon={<RollbackOutlined />}>
                    Proses Reversal
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* Transaction Detail Modal */}
      <Modal
        title="Detail Transaksi"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedTransaction && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="ID Transaksi" span={2}>
                {selectedTransaction.transaction.transaction_id}
              </Descriptions.Item>
              <Descriptions.Item label="Order ID Mitra" span={2}>
                {selectedTransaction.transaction.external_id || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(selectedTransaction.transaction.status)}>
                  {getStatusText(selectedTransaction.transaction.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Metode Pembayaran">
                {selectedTransaction.transaction.payment_method}
              </Descriptions.Item>
              <Descriptions.Item label="Jumlah">
                {formatCurrency(selectedTransaction.transaction.amount)}
              </Descriptions.Item>
              <Descriptions.Item label="Fee">
                {formatCurrency(selectedTransaction.transaction.fee || 0)}
              </Descriptions.Item>
              <Descriptions.Item label="Total" span={2}>
                <strong>{formatCurrency((parseFloat(selectedTransaction.transaction.amount) || 0) + (parseFloat(selectedTransaction.transaction.fee) || 0))}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Merchant" span={2}>
                {selectedTransaction.transaction.merchant_name}
              </Descriptions.Item>
              <Descriptions.Item label="ID Pelanggan" span={2}>
                {selectedTransaction.transaction.customer_no || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Nama Pelanggan" span={2}>
                {selectedTransaction.transaction.destination_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Email Mitra" span={2}>
                {selectedTransaction.transaction.customer_email || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Deskripsi" span={2}>
                {selectedTransaction.transaction.description || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Dibuat">
                {moment(selectedTransaction.transaction.created_at).format('DD/MM/YYYY HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="Dibayar">
                {selectedTransaction.transaction.paid_at
                  ? moment(selectedTransaction.transaction.paid_at).format('DD/MM/YYYY HH:mm:ss')
                  : '-'}
              </Descriptions.Item>
            </Descriptions>

            {selectedTransaction.logs && selectedTransaction.logs.length > 0 && (
              <div style={{ marginTop: '24px' }}>
                <h4>Riwayat Status</h4>
                <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
                  {selectedTransaction.logs.map((log, index) => (
                    <div key={index} style={{ marginBottom: '8px' }}>
                      <div style={{ fontWeight: 'bold' }}>{log.status}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {moment(log.created_at).format('DD/MM/YYYY HH:mm:ss')}
                      </div>
                      {log.message && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          {log.message}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Transactions;